import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useCreateModule } from '../../../hooks/subscriptions';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { ModuleCategory } from '../../../types/subscriptions';
import { getCategoryLabel } from '../../../utils/subscriptions';
import type { CreateModuleDto } from '../../../types/subscriptions';

export const ModuleCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const createMutation = useCreateModule();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreateModuleDto>({
    defaultValues: {
      category: ModuleCategory.CORE,
      dependencies: [],
      isCore: false,
    },
  });

  const onSubmit = (data: CreateModuleDto) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        showMessage('Module created successfully', 'success');
        navigate('/subscriptions/modules');
      },
      onError: (error: any) => {
        showMessage(error.response?.data?.message || 'Failed to create module', 'error');
      },
    });
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Create Feature Module
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Module name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Module Name"
                  placeholder="e.g., Advanced Analytics"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('key', {
                    required: 'Key is required',
                    pattern: {
                      value: /^[a-z0-9_]+$/,
                      message: 'Key must be lowercase letters, numbers, and underscores only'
                    }
                  })}
                  error={!!errors.key}
                  helperText={errors.key?.message || 'Unique identifier (e.g., advanced_analytics)'}
                  fullWidth
                  label="Module Key"
                  placeholder="e.g., advanced_analytics"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  placeholder="Describe what this module provides"
                />
              </Grid>

              {/* Category */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Category
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel>Category</InputLabel>
                  <Controller
                    name="category"
                    control={control}
                    rules={{ required: 'Category is required' }}
                    render={({ field }) => (
                      <Select {...field} label="Category">
                        {Object.values(ModuleCategory).map((category) => (
                          <MenuItem key={category} value={category}>
                            {getCategoryLabel(category)}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.category && (
                    <FormHelperText>{errors.category.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Module Settings */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Module Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="isCore"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">Core Module</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Core modules are always available and cannot be disabled
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                />
              </Grid>

              {/* Dependencies Section */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Dependencies
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Dependencies can be configured after creation by editing the module
                </Typography>
              </Grid>
            </Grid>

            <Box display="flex" gap={2} mt={4}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Module'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/subscriptions/modules')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
